import React, { createContext, useState, useEffect } from 'react';
import jwtDecode from 'jwt-decode';
import moment from 'moment';
import { buildErrorMessage } from 'src/api/ApiService';
import { baseApiCall } from 'src/api/Api';
import { user_service } from 'src/constants/url';
import { routes_all } from 'src/constants/constVariables';
import { useDispatch } from 'react-redux';
import { AuthToken, accesslist } from 'src/redux/features';
import { resetstate } from 'src/redux';
import config from 'src/config/config';

const initializeScript = (src) =>
	new Promise((resolve, reject) => {
		const script = document.createElement('script');
		script.src = src;
		script.onload = () => resolve();
		script.onError = (e) => reject(e);
		document.body.appendChild(script);
	});

const { AUTH_ENDPOINT, UI_BASE_URL } = config;

export const AuthorizationContext = createContext(null);

const AuthorizationHandler = (props) => {
	const dispatch = useDispatch();
	const SSO_SESSION_CHECK_INTERVAL_MS = 60000;
	
	const [remainingTime, setRemainingTime] = useState(null);
	const [refresh, setRefresh] = useState(false);
	const [access, setAccess] = useState({});
	const [isloader, setLoader] = useState(true);
	
	// For keycloak authentication use below states

	const [user, setUser] = useState({ isLoaded: false });
	const [keycloak, setKeycloak] = useState(null);
	const [isAuthenticated, setIsAuthenticated] = useState(false);
	const [authToken, setAuthToken] = useState(null);

	// End keycloak authentication
	
	// Use bypass keycloak authentication use below states (Locally)

	// const [user, setUser] = useState({
	// 	name: 'Sanket Pawar',
	// 	email: 'sanketp.ss@tatamotors.com',
	// 	plants: ['1100'],
	// 	user_role: ['PVBU_LOGISTICS_PACKAGING_ADMIN'],
	//  	// 'CVBU_LOGISTICS_PACKAGING_ADMIN'
	// 	// 'CVBU_LOGISTICS_PACKAGING_SUPPLIER_USER'
	// 	// 'CVBU_LOGISTICS_PACKAGING_QA_USER'
	// });
	// const [keycloak, setKeycloak] = useState(null);
	// const [isAuthenticated, setIsAuthenticated] = useState(true);
	// const [authToken, setAuthToken] = useState('dfadfaddf54ad5fa5df5adf5ad');

	// End keycloak authentication (Locally)

	const checkSSOSessionStatePeriodically = () => {
		if (localStorage.getItem('stopApiCalls') === 'false') {
			keycloak
				.loadUserInfo()
				.then(() =>
					setTimeout(
						checkSSOSessionStatePeriodically,
						SSO_SESSION_CHECK_INTERVAL_MS
					)
				)
				.catch((error) => {
					const { exp, iat, auth_time: authTime } = keycloak?.tokenParsed;
					console.error(
						`failed user check, error:${buildErrorMessage(
							error
						)}; exp:${getISTTime(exp)} iat:${getISTTime(iat)} ` +
							`auth:${getISTTime(authTime)}`
					);
					refreshTokenAndContinueUserSessionCheckElseLogout();
				});
		}
	};

	const refreshTokenAndContinueUserSessionCheckElseLogout = () => {
		refreshToken()
			.then(() => {
				checkSSOSessionStatePeriodically();
			})
			.catch((error) => {
				console.error({ error });
				console.error(
					`Failed to refresh the token, or the session has expired.Err:${buildErrorMessage(
						error
					)} Logging user out...`
				);
				console.error(buildErrorMessage(error));
				logoutUser();
			});
	};

	const generateUser = async (token) => {
		try {
			const {
				name,
				email,
				sub: userID,
				plants,
				logistics_packaging_functions,
			} = jwtDecode(token);
			return {
				name,
				email,
				userID,
				plants,
				isLoaded: true,
				user_role: logistics_packaging_functions,
			};
		} catch (err) {
			console.log('error');
		}
	};

	const loadAuthData = async (token) => {
		const user = await generateUser(token);
		if (!user?.plants || !user?.user_role) {
			setIsAuthenticated(false);
			setLoader(false);
		} else {
			setUser(user);
			setAuthToken(token);
			dispatch(AuthToken(token));
		}
	};

	const logoutUser = () => {
		localStorage.setItem('stopApiCalls', false);
		logoutAndClearData(keycloak);
		setIsAuthenticated(false);
		setAuthToken(null);
		setUser([]);
		dispatch(resetstate());
		setLoader(true);
	};

	const refreshToken = () =>
		keycloak.updateToken(-1).then(() => {
			setAuthToken(keycloak.token);
			dispatch(AuthToken(keycloak.token));
		});

	const fecthUserRoleAccessList = () => {
		baseApiCall({
			url: user_service.GET_USER_ACCESS_LIST,
			requestBody: {
				role: user?.user_role,
			},
			method: 'POST',
			onSuccess: (res) => {
				if (res && res.data) {
					const data = res.data.result;
					setAccess(data);
					dispatch(accesslist(data));
					setLoader(false);
				}
			},
			onFailure: (res) => {
				setLoader(false);
			},
			invalidCall: (res) => {
				setLoader(false);
			},
			errorCall: (res) => {
				setLoader(false);
			},
		});
	};

	useEffect(() => {
		if (refresh) {
			refreshToken().catch((err) =>
				console.error(`failed to refresh token ${err}`)
			);
			setRefresh(false);
		}
	}, [refresh]);

	// API CALL -- Comment below section for locally use

	useEffect(() => {
		if (!authToken) return;
		const expirtytimeInUnixSeconds = keycloak.tokenParsed.exp;
		const currentTimeInUnixSeconds = moment().unix();
		const sixtySeconds = 60;
		const remainingTimeToRefreshInMS =
			(expirtytimeInUnixSeconds - currentTimeInUnixSeconds - sixtySeconds) *
			1000;
		setRemainingTime(remainingTimeToRefreshInMS);
	}, [authToken]);

	useEffect(() => {
		initializeScript(`${AUTH_ENDPOINT}/js/keycloak.js`)
			.then(() => setKeycloak(window.Keycloak('/keycloak.json')))
			.catch(() => console.error('failed to load keycloak script'));
	}, []);

	useEffect(() => {
		if (!keycloak) return;
		keycloak
			.init({
				onLoad: 'login-required',
				promiseType: 'native',
				pkceMethod: 'S256',
				checkLoginIframe: false,
			})
			.then((authenticated) => {
				window.keycloak = keycloak;
				setIsAuthenticated(authenticated);
				if (authenticated) {
					loadAuthData(keycloak.token);
					checkSSOSessionStatePeriodically();
				}
			})
			.catch((error) => console.info('keycloack init error:', error));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [keycloak]);

	// END API CALL -- locally use

	useEffect(() => {
		if (user?.user_role) {
			fecthUserRoleAccessList();
		}
	}, [user?.user_role]);

	const generateNavigationPaths = (backendKeys, navigationPaths) => {
		const pathMapping = {};

		for (const backendKey in backendKeys) {
			pathMapping[backendKey] = backendKeys[backendKey].map((item) => {
				const matchingPath = navigationPaths.find(
					(pathConfig) => pathConfig.module_code === item.key
				);
				return matchingPath ? matchingPath.path : null;
			});
		}
		return pathMapping;
	};

	const route_privilege = generateNavigationPaths(access, routes_all);

	const authData = {
		user,
		isAuthenticated,
		logoutUser,
		authToken,
		remainingTime,
		access,
		route_privilege,
		setRefresh,
		setRemainingTime,
		isloader,
	};

	return <AuthorizationContext.Provider value={authData} {...props} />;
};
const logoutAndClearData = (keycloak) =>
	keycloak
		.logout({ redirectUri: UI_BASE_URL })
		.then(() => {
			window.keycloak = null;
			window.location.href = '/';
			window.localStorage.clear();
		});

const getISTTime = (unixTimeInSeconds) =>
	moment.unix(unixTimeInSeconds).format('hh:mm:ssa');

export default AuthorizationHandler;
